import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ModuleHeader from "../components/ModuleHeader";
import { Table } from "antd";
import { uamApi } from "@/api/uamApi";
import { rem2px } from "@/utils/fontSize";
import ReactQRCode from "react-qr-code";
import { REM_CONFIG } from "@/utils/fontSize";
// ==========================
// 3. 核心组件（rem 适配改造）
// ==========================
const SharedAssets = ({ className = "", initDate = "2025-09", }) => {
    // 基于 rem 系数的动态行高
    const ROW_HEIGHT = rem2px(REM_CONFIG.rowHeight);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(initDate);
    const [tableHeight, setTableHeight] = useState("auto");
    const [isScrolling, setIsScrolling] = useState(true);
    // 二维码弹窗状态
    const [showQrPopup, setShowQrPopup] = useState(false);
    const [currentAssetCode, setCurrentAssetCode] = useState("");
    const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
    const tableContainerRef = useRef(null);
    const scrollTimer = useRef(null);
    // ✅ 修复1：初始化空数组，无默认空白行
    const [tableData, setTableData] = useState([]);
    // 二维码鼠标移入事件
    const handleQrMouseEnter = (e, code) => {
        setCurrentAssetCode(code);
        setPopupPos({ x: e.clientX + 15, y: e.clientY + 15 });
        setShowQrPopup(true);
        setIsScrolling(false);
    };
    // 二维码鼠标移出事件
    const handleQrMouseLeave = () => {
        setShowQrPopup(false);
        setIsScrolling(true);
    };
    const columns = [
        {
            title: "序号",
            key: "index",
            width: "18%",
            render: (_, __, index) => index + 1,
        },
        {
            title: "资产编号",
            dataIndex: "assetCode",
            key: "assetCode",
            width: "40%",
            align: "left",
            ellipsis: true,
        },
        {
            title: "资产名称",
            dataIndex: "assetName",
            key: "assetName",
            ellipsis: true,
            width: "40%",
            align: "left",
        },
        {
            title: "二维码",
            dataIndex: "assetCode",
            key: "assetCode",
            width: "22%",
            align: "center",
            render: (code) => (_jsx("div", { style: {
                    width: "100%",
                    height: `0.34rem`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                }, onMouseEnter: (e) => handleQrMouseEnter(e, code), onMouseLeave: handleQrMouseLeave, children: _jsx(ReactQRCode, { value: code, size: 20, fgColor: "#00ffff", bgColor: "transparent" }) })),
        },
    ];
    useEffect(() => {
        getData();
    }, []);
    /**
     * @description: getData - 获取数据
     */
    const getData = async () => {
        try {
            setIsLoading(true);
            const res = await uamApi.shareAssets();
            setTableData(res.data || []);
        }
        catch (error) {
            setTableData([]);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        const calculateTableHeight = () => {
            if (!tableContainerRef.current)
                return;
            const container = tableContainerRef.current;
            const parent = container.parentElement;
            if (!parent)
                return;
            // 1. 计算顶部固定高度（ModuleHeader + 单位文本 + 间距）
            const header = container.previousElementSibling; // ModuleHeader 元素
            const headerHeight = header?.offsetHeight || 0;
            const unitText = container.querySelector("p"); // 单位文本元素
            const unitTextHeight = unitText ? unitText.offsetHeight : 0;
            const topSpacing = rem2px(REM_CONFIG.spacing.top); // 顶部间距（原 16px → 0.16rem）
            const totalTopHeight = headerHeight + unitTextHeight + topSpacing;
            // 2. 父容器可用高度（基于 rem 适配后的父容器高度）
            const parentHeight = parent.offsetHeight;
            const availableHeight = parentHeight - totalTopHeight;
            // 3. 表格内容高度（基于 rem 行高 × 数据行数）
            const contentHeight = tableData.length * ROW_HEIGHT;
            // 4. 动态设置表格高度（保持原逻辑）
            if (contentHeight <= availableHeight || availableHeight < rem2px(0.1)) {
                // 最小可用高度 0.1rem（避免异常）
                setTableHeight("auto");
            }
            else {
                setTableHeight(Math.floor(availableHeight));
            }
        };
        calculateTableHeight();
        // 监听容器尺寸变化，确保响应式适配
        const resizeObserver = new ResizeObserver(calculateTableHeight);
        requestAnimationFrame(() => {
            if (tableContainerRef.current) {
                resizeObserver.observe(tableContainerRef.current);
            }
        });
        return () => resizeObserver.disconnect();
    }, [tableData, ROW_HEIGHT]);
    useEffect(() => {
        if (scrollTimer.current) {
            clearInterval(scrollTimer.current);
            scrollTimer.current = null;
        }
        if (tableHeight === "auto" || !isScrolling || tableData.length === 0) {
            return;
        }
        const scrollContainer = tableContainerRef.current?.querySelector(".ant-table-body");
        if (!scrollContainer)
            return;
        scrollTimer.current = setInterval(() => {
            const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
            const scrollStep = REM_CONFIG.spacing.scrollStep; // 滚动步长（像素，无需 rem 适配）
            let newScrollTop = scrollTop + scrollStep;
            // 接近底部时重置（避免最后一行截断，基于 rem 适配后的高度计算）
            if (newScrollTop + clientHeight >= scrollHeight - 1) {
                newScrollTop = 0;
            }
            scrollContainer.scrollTop = newScrollTop;
        }, 50); // 滚动频率（时间单位，无需 rem 适配）
        return () => {
            if (scrollTimer.current) {
                clearInterval(scrollTimer.current);
            }
        };
    }, [tableHeight, isScrolling, tableData.length]);
    // 鼠标悬停控制（保持原逻辑）
    const handleMouseEnter = () => {
        setIsScrolling(false);
    };
    const handleMouseLeave = () => {
        setIsScrolling(true);
    };
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.2)] 
        flex flex-col 
        ${className}
      `, children: [_jsx(ModuleHeader, { title: "\u5171\u4EAB\u8D44\u4EA7" }), _jsxs("div", { ref: tableContainerRef, className: "custom-table-container flex-1 w-full h-full min-h-0", style: {
                    minHeight: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    paddingLeft: `${REM_CONFIG.spacing.paddingX}rem`,
                    paddingRight: `${REM_CONFIG.spacing.paddingX}rem`,
                }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx(Table, { columns: columns, dataSource: tableData, pagination: false, scroll: {
                            y: tableHeight,
                            x: "100%",
                        }, style: {
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                        }, onRow: (record, index) => ({
                            style: {
                                backgroundColor: index % 2 === 0 ? "transparent" : "#062654",
                                height: `${ROW_HEIGHT}px`,
                            },
                        }) }), showQrPopup && (_jsxs("div", { style: {
                            position: "fixed",
                            left: popupPos.x,
                            top: popupPos.y,
                            background: "rgba(6, 42, 88, 0.95)",
                            padding: "16px",
                            borderRadius: "6px",
                            boxShadow: "0 0 12px rgba(0,255,255,0.3)",
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "12px",
                        }, children: [_jsx(ReactQRCode, { value: currentAssetCode, size: 180, fgColor: "#00ffff", bgColor: "transparent" }), _jsx("span", { style: { color: "#fff", fontSize: "16px" }, children: currentAssetCode })] })), _jsx("style", { children: `
            /* 表格整体样式 */
            .custom-table-container .ant-table,
            .custom-table-container .ant-table table {
              background-color: transparent !important;
              color: #FFFFFF !important;
              border: none !important;
              border-collapse: collapse !important;
              border-spacing: 0 !important;
            }

            /* 表头单元格（适配 rem 行高、内边距） */
            .custom-table-container .ant-table-thead > tr > th {
              background-color: #062a58 !important;
              color: #00ffff !important;
              border: none !important; 
              border-right: 1px solid #062a58 !important; 
              border-left: 1px solid #062a58 !important;
              font-weight: 600;
              height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
            }

            /* 移除首尾列边框 */
            .custom-table-container .ant-table-thead > tr > th:first-child {
              border-left: none !important;
            }
            .custom-table-container .ant-table-thead > tr > th:last-child {
              border-right: none !important;
            }

            /* 内容区单元格（适配 rem 行高、内边距、字体） */
            .custom-table-container .ant-table-tbody > tr > td {
              color: #FFFFFF !important;
              border: none !important;
              height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
            }

            /* 悬停效果（保持原逻辑） */
            .custom-table-container .ant-table-tbody > tr:hover > td {
              background-color: #1A446E !important;
            }

            /* 滚动容器（保持原逻辑） */
            .custom-table-container .ant-table-body {
              scrollbar-width: none;
              -ms-overflow-style: none;
              border: none !important;
              overflow-y: auto !important;
              height: auto !important;
              min-height: 0 !important;
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            .custom-table-container .ant-table-body::-webkit-scrollbar {
              display: none;
            }
            .custom-table-container .ant-table-container {
              border: none !important;
            }

            /* 移除分隔线（保持原逻辑） */
            .custom-table-container .ant-table-thead::after,
            .custom-table-container .ant-table-cell::before {
              display: none !important;
            }

            /* ✅ 核心修复：隐藏Antd测量空行 */
            .custom-table-container .ant-table-measure-row {
              display: none !important;
              height: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
            }

            /* ✅ 核心修复：无数据时隐藏占位，保留表头 */
            .custom-table-container .ant-table-placeholder {
              display: none !important;
              height: 0 !important;
            }

            /* ✅ 清除tbody顶部间距，数据紧贴表头 */
            .custom-table-container .ant-table-tbody {
              margin-top: 0 !important;
              padding-top: 0 !important;
              border-top: none !important;
            }
          ` })] })] }));
};
export default SharedAssets;
